import { Link, Navigate, useLocation } from "react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth-provider";
import { Skeleton } from "@/components/skeleton";

interface Props {
  children: React.ReactNode;
}

/** Maximum auto-retries for missing-organization before showing manual recovery. */
const MAX_AUTO_RETRIES = 3;

/** Delay between auto-retries in ms. */
const AUTO_RETRY_DELAY_MS = 2000;

/** Redirects to login if unauthenticated. Auto-retries workspace resolution for new signups. */
export function ProtectedRoute({ children }: Props) {
  const {
    authCheckError,
    bootstrapMessage,
    bootstrapState,
    isAuthenticated,
    isLoading,
    refresh,
    signOut,
  } = useAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Auto-retry when workspace provisioning hasn't settled yet
  useEffect(() => {
    if (
      bootstrapState !== "missing-organization" ||
      !isAuthenticated ||
      isLoading ||
      autoRetryCount >= MAX_AUTO_RETRIES
    ) {
      return;
    }

    setIsRetrying(true);
    retryTimerRef.current = setTimeout(() => {
      void refresh().then(() => {
        setAutoRetryCount((prev) => prev + 1);
        setIsRetrying(false);
      });
    }, AUTO_RETRY_DELAY_MS);

    return clearRetryTimer;
  }, [bootstrapState, isAuthenticated, isLoading, autoRetryCount, refresh, clearRetryTimer]);

  // Reset auto-retry counter when bootstrap succeeds
  useEffect(() => {
    if (bootstrapState === "ready") {
      setAutoRetryCount(0);
    }
  }, [bootstrapState]);

  // No flash of login page -- show nothing until session check resolves
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="w-full space-y-2">
                <Skeleton className="mx-auto h-4 w-40" />
                <Skeleton className="mx-auto h-4 w-56" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authCheckError && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm">
          <p className="text-sm font-semibold text-foreground">Connection issue</p>
          <h1 className="mt-2 text-2xl font-bold text-foreground">Could not reach the server</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            We could not verify your session. This is usually a temporary network issue, and your work should still be there once the connection recovers.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Try again
            </button>
            <Link
              to="/login"
              state={{ from: returnTo }}
              replace
              className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground"
            >
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: returnTo }} replace />;
  }

  if (bootstrapState !== "ready") {
    const isProvisioningPending = bootstrapState === "missing-organization";
    const isAutoRetrying = isProvisioningPending && autoRetryCount < MAX_AUTO_RETRIES;

    if (isAutoRetrying || isRetrying) {
      return (
        <div className="flex min-h-screen items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              <div className="flex flex-col items-center gap-4 text-center">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-44" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    const title = isProvisioningPending
      ? "Workspace setup is taking longer than expected"
      : "Workspace setup needs attention";

    const message = isProvisioningPending
      ? "Your account is ready, but the workspace is still finishing setup. Retry in a few seconds. We will take you into the guided first-run flow as soon as it is available."
      : (bootstrapMessage ?? "We hit a problem while opening your workspace. Retry to continue or sign out and come back in a moment.");

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">{message}</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                setAutoRetryCount(0);
                void refresh();
              }}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Retry workspace setup
            </button>
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/** Role string → numeric level mapping (matches Better Auth role hierarchy). */
const ROLE_LEVELS: Record<string, number> = {
  super_admin: 8,
  admin: 7,
  customer_success: 6,
  staff: 6,
  teacher: 5,
  affiliate: 4,
  premium: 4,
  paid: 3,
  free: 2,
  unregistered: 1,
};

const MIN_ADMIN_LEVEL = 7;

/** Wraps ProtectedRoute with an additional admin role check (level >= 7). */
export function AdminProtectedRoute({ children }: Props) {
  const { user } = useAuth();

  return (
    <ProtectedRoute>
      <AdminGate user={user}>{children}</AdminGate>
    </ProtectedRoute>
  );
}

function AdminGate({ user, children }: { user: { role?: string } | null; children: React.ReactNode }) {
  const location = useLocation();
  const level = ROLE_LEVELS[user?.role ?? ""] ?? 0;

  if (level < MIN_ADMIN_LEVEL) {
    const params = new URLSearchParams({
      resource: "Admin dashboard",
      requiredRole: "super_admin",
      returnTo: `${location.pathname}${location.search}${location.hash}`,
    });
    return <Navigate to={`/permission-denied?${params.toString()}`} replace />;
  }

  return <>{children}</>;
}
